import 'bootstrap/dist/css/bootstrap.min.css';
import './app.scss';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import NavBar from '../../components/nav-bar';
import Notifications from '../../components/notifications';
import { PATH } from '../../constants';
import ErrorBoundary from '../../submodules/shared/components/error-boundary/error-boundary';
import { navigate } from '../../submodules/shared/utils/navigator';
import HomeContainer from '../../views/home';
import LabContainer from '../../views/lab';

import NavigatorBridge from './navigator-bridge';

// Wraps a route element in an ErrorBoundary keyed by the current location, so
// a caught error on one URL does not bleed into the next (XHYD-1883 invariant).
// In v5 this was achieved via `<Route render={routeProps => <ErrorBoundary
// key={location.key} />}>`; v6/v7 don't pass route props, so the wrapper reads
// `useLocation()` itself.
const RouteBoundary = ({ name, children }) => {
    const { pathname, search, hash } = useLocation();
    return (
        <ErrorBoundary key={`${pathname}${search}${hash}`} name={name}>
            {children}
        </ErrorBoundary>
    );
};

RouteBoundary.propTypes = {
    name: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};

// v6/v7 dropped the v5 behavior of injecting `match` as a prop on the routed
// element. The Lab tree (Lab.jsx + LabView + LabEdit) still reads
// `this.props.match.params` and forwards `match` via R.pick, so this adapter
// synthesizes a v5-shaped `{ params }` object instead of cascading a class-to-
// hooks rewrite through three files.
const LabRoute = () => {
    const params = useParams();
    return <LabContainer match={{ params }} />;
};

class App extends Component {
    componentDidMount() {
        this.props.bootstrapAppAccess();
    }

    handleNavClick({ target }) {
        navigate(target.dataset.path);
    }

    render() {
        const { messages, hideNotificationItem } = this.props;

        return (
            <div className="App">
                <BrowserRouter>
                    <NavigatorBridge />
                    <div className="router">
                        <NavBar handleNavClick={this.handleNavClick} />
                        <Notifications messages={messages} hideNotificationItem={hideNotificationItem} />
                        <Routes>
                            <Route
                                path={`${PATH.home}/*`}
                                element={
                                    <RouteBoundary name="route:home">
                                        <HomeContainer />
                                    </RouteBoundary>
                                }
                            />
                            <Route
                                path={PATH.lab}
                                element={
                                    <RouteBoundary name="route:lab">
                                        <LabRoute />
                                    </RouteBoundary>
                                }
                            />
                            <Route
                                path={PATH.labAction()}
                                element={
                                    <RouteBoundary name="route:lab-action">
                                        <LabRoute />
                                    </RouteBoundary>
                                }
                            />
                            <Route path="*" element={<Navigate to={PATH.home} replace />} />
                        </Routes>
                    </div>
                </BrowserRouter>
            </div>
        );
    }
}

App.propTypes = {
    messages: PropTypes.array,
    hideNotificationItem: PropTypes.func,
    bootstrapAppAccess: PropTypes.func,
};

export default App;
